// Pure calculation logic for the AI Analytics calculator (formulas unchanged from the original UI).

export const CAMERA_GROUPS = [
  {
    id: 'detection',
    name: 'Object Detection',
    fps: 0.5,
    examples: 'Vehicle detection, PPE, Fire & Smoke, Zone intrusion, Helmet, Crowd, Queue, Mobile…',
  },
  { id: 'tailgating', name: 'Tailgating / Vehicle count', fps: 5, examples: 'Vehicle counting with tailgating, Wrong-way driving' },
  { id: 'alpr', name: 'ALPR (Number plate)', fps: 0.5, examples: 'Number plate detection & recognition' },
  { id: 'tamper', name: 'Camera Tamper', fps: 0.5, examples: 'Tamper and camera fault only' },
  { id: 'invigilator', name: 'Invigilator not moving', fps: 3, examples: '' },
  { id: 'speed', name: 'Speed detection', fps: 3, examples: '' },
  { id: 'fr', name: 'Face Recognition (FR)', fps: 3, examples: 'FR alert' },
  { id: 'entryExit', name: 'Entry-Exit / Vehicle count', fps: 3, examples: 'Entry Exit, Idle time alert' },
];

export const NUC_MODELS = [
  { id: 'nuc1', name: 'NUC12i3', vcpu: 8, ram: 16, type: 'nuc' },
  { id: 'nuc2', name: 'NUC12i5', vcpu: 16, ram: 32, type: 'nuc' },
  { id: 'nuc3', name: 'NUC12i7', vcpu: 20, ram: 40, type: 'nuc' },
  { id: 'nuc4', name: 'NUC12i9', vcpu: 24, ram: 48, type: 'nuc' },
  { id: 'nuc5', name: 'NUC13i9', vcpu: 32, ram: 64, type: 'nuc' },
];

export const SERVER_MODELS = [
  { id: 'srv1', name: '2nd Gen Intel Xeon Silver 4216 Processor', vcpu: 32, ram: 64, type: 'server' },
  { id: 'srv2', name: 'Intel Xeon Gold 6226R Processor', vcpu: 32, ram: 64, type: 'server' },
  { id: 'srv3', name: 'Intel Core i9-13900K Processor', vcpu: 32, ram: 64, type: 'server' },
  { id: 'srv4', name: 'Intel Xeon Gold 6230 Processor', vcpu: 40, ram: 80, type: 'server' },
  { id: 'srv5', name: 'Intel Xeon Silver 4316 (20C/40T)', vcpu: 40, ram: 80, type: 'server' },
  { id: 'srv6', name: '13th Gen Intel Core i7 Processors', vcpu: 24, ram: 48, type: 'server' },
  { id: 'srv7', name: 'Intel Xeon Gold 6240R Processor', vcpu: 48, ram: 96, type: 'server' },
  { id: 'srv8', name: 'Intel Xeon Gold 5420+ (28C/56T)', vcpu: 56, ram: 112, type: 'server' },
  { id: 'srv9', name: 'Intel Xeon Gold 6433N Processor', vcpu: 64, ram: 128, type: 'server' },
  { id: 'srv10', name: 'Intel Xeon Platinum 8352V CPU (36 core / 2.1G)', vcpu: 72, ram: 144, type: 'server' },
  { id: 'srv11', name: 'Intel Xeon Platinum 8480+ Processor', vcpu: 112, ram: 224, type: 'server' },
];

// Sorted copies (the original sorted the constant in place on every render).
const SERVERS_ASC = [...SERVER_MODELS].sort((a, b) => a.vcpu - b.vcpu);
export const ALL_MODELS = [...NUC_MODELS, ...SERVER_MODELS];

export const getModelsForSetup = (setupType) =>
  setupType === 'single' ? [...NUC_MODELS, ...SERVERS_ASC].sort((a, b) => a.vcpu - b.vcpu) : SERVERS_ASC;

// ---- Core vCPU calculation ----
export function calculateCores(counts, enableGPU) {
  const n = (k) => Number(counts[k]) || 0;
  const detection = n('detection');
  const tailgating = n('tailgating');
  const alpr = n('alpr');
  const tamper = n('tamper');
  const invigilator = n('invigilator');
  const speed = n('speed');
  const fr = n('fr');
  const entryExit = n('entryExit');

  const detectionBackendVcore = detection > 0 ? Math.ceil(detection / 2.5) : 0;
  const detectionFPS = detection * 0.5;
  const detectionInferencingVcoreBase = Math.ceil(detectionFPS / 20);
  const detectionInferencingVcore = enableGPU ? 0 : detectionInferencingVcoreBase;
  const detectionGPUL4 = detection > 0 ? Math.ceil(detection / 750) : 0;

  const tailgatingVcore = tailgating > 0 ? tailgating * 4 : 0;
  const alprVcore = alpr > 0 ? Math.ceil((alpr / 2) * 2) : 0;
  const tamperVcore = tamper > 0 ? Math.ceil(tamper / 5) * 2 : 0;
  const invigilatorVcore = invigilator > 0 ? Math.ceil(invigilator / 1) : 0;
  const speedVcore = speed > 0 ? Math.ceil(speed * 2) : 0;

  const frFPS = fr * 3;
  const frInferencingVcoreBase = fr > 0 ? Math.ceil((fr / 13) * 4) : 0;
  const frBackendVcore = fr > 0 ? Math.ceil(fr / 2) * 2 : 0;
  const frInferencingVcore = enableGPU ? 0 : frInferencingVcoreBase;

  const entryExitVcore = entryExit > 0 ? Math.ceil(entryExit * 2) : 0;
  const k8sVcore = 3;

  const gpuL4 = detection > 0 && enableGPU ? detectionGPUL4 : 0;
  const gpu4090 = fr > 0 && enableGPU ? Math.ceil(fr / 40) : 0;

  const fixed =
    detectionBackendVcore + tailgatingVcore + alprVcore + tamperVcore + invigilatorVcore +
    speedVcore + frBackendVcore + entryExitVcore + k8sVcore;
  const totalVCPU = Math.ceil(fixed + detectionInferencingVcore + frInferencingVcore);
  const cpuOnlyBaseVCPU = Math.ceil(fixed + detectionInferencingVcoreBase + frInferencingVcoreBase);

  // Total FPS: cameras x FPS of each group
  const totalFPS = CAMERA_GROUPS.reduce((sum, g) => sum + n(g.id) * g.fps, 0);
  const totalCameras = CAMERA_GROUPS.reduce((sum, g) => sum + Math.trunc(n(g.id)), 0);

  return {
    totalVCPU, cpuOnlyBaseVCPU, totalFPS, totalCameras, gpuL4, gpu4090,
    detectionBackendVcore, detectionInferencingVcore, detectionInferencingVcoreBase, detectionFPS,
    tailgatingVcore, alprVcore, tamperVcore, invigilatorVcore, speedVcore,
    frInferencingVcore, frInferencingVcoreBase, frBackendVcore, frFPS,
    entryExitVcore, k8sVcore,
  };
}

// ---- Storage ----
export const getAlertsPerDay = (totalCameras) => {
  if (totalCameras <= 50) return 5000;
  if (totalCameras <= 100) return 10000;
  if (totalCameras <= 200) return 20000;
  return 30000;
};

export function getStorage(totalCameras, months) {
  const alertsPerDay = getAlertsPerDay(totalCameras);
  const gbPerDay = (alertsPerDay * 250 * 1024) / (1024 * 1024 * 1024);
  const imageGB = gbPerDay * 30 * months;
  const baseSystemGB = 600;
  const totalWithoutBuffer = baseSystemGB + imageGB;
  const totalWithBufferGB = totalWithoutBuffer * 1.3;
  return { alertsPerDay, gbPerDay, baseSystemGB, imageGB, totalWithoutBuffer, totalWithBufferGB };
}

export function getNearestSSDSize(requiredGB) {
  const options = [750, 1000, 1500, 2000, 3000, 4000, 6000, 8000, 12000, 15000];
  const match = options.find((s) => s >= requiredGB);
  if (!match) return '15TB+ (data-center tier)';
  return match >= 1000 ? `${match / 1000}TB` : `${match}GB`;
}

// ---- Hardware selection ----
export function recommendHardware(vcpu) {
  const nuc = NUC_MODELS.find((m) => m.vcpu >= vcpu);
  if (nuc && vcpu <= 32) return { setupType: 'single', model: nuc };
  const single = SERVERS_ASC.find((s) => s.vcpu >= vcpu);
  if (single && vcpu <= 112) return { setupType: 'single', model: single };
  const dual = SERVERS_ASC.find((s) => s.vcpu * 2 >= vcpu);
  if (dual && vcpu <= 224) return { setupType: 'dual', model: dual };
  return { setupType: 'multi', model: SERVERS_ASC[SERVERS_ASC.length - 1], processorType: 'dual' };
}

// overrides: { setupType, modelId, slaveCount, processorType } - null/undefined = auto
export function selectHardware(requiredVCPU, overrides = {}) {
  const rec = recommendHardware(requiredVCPU);
  const setupType = overrides.setupType || rec.setupType;
  const processorType =
    overrides.processorType || (setupType === rec.setupType && rec.processorType) || 'dual';
  const mult = processorType === 'dual' ? 2 : 1;

  let model = overrides.modelId ? ALL_MODELS.find((m) => m.id === overrides.modelId) : null;
  let slaveCount = overrides.slaveCount || null;

  if (setupType === 'single') {
    model = model || NUC_MODELS.find((n) => n.vcpu >= requiredVCPU) || SERVERS_ASC.find((s) => s.vcpu >= requiredVCPU);
  } else if (setupType === 'dual') {
    model = model || SERVERS_ASC.find((s) => s.vcpu * 2 >= requiredVCPU);
  } else {
    if (model && !slaveCount) slaveCount = Math.ceil(requiredVCPU / (model.vcpu * mult));
    if (!model && slaveCount) {
      model = SERVERS_ASC.find((s) => s.vcpu * mult >= Math.ceil(requiredVCPU / slaveCount));
    }
    if (!model) model = SERVERS_ASC[SERVERS_ASC.length - 1];
    if (!slaveCount) slaveCount = Math.max(1, Math.ceil(requiredVCPU / (model.vcpu * mult)));
  }
  // nothing big enough: fall back to biggest so a compatibility warning is shown
  if (!model) model = SERVERS_ASC[SERVERS_ASC.length - 1];

  let effectiveVCPU;
  if (setupType === 'dual') effectiveVCPU = model.vcpu * 2;
  else if (setupType === 'multi') effectiveVCPU = 8 + slaveCount * model.vcpu * mult; // 8 = master node
  else effectiveVCPU = model.vcpu;

  return { setupType, processorType, model, slaveCount: slaveCount || 0, effectiveVCPU, compatible: effectiveVCPU >= requiredVCPU };
}

// ---- Text output ----
export function buildSummary({ cores, hw, requiredVCPU, storage, splitStorage, enableGPU, bufferPercentage, baseVCPU }) {
  const ssd = getNearestSSDSize(storage.totalWithBufferGB);
  const { model, setupType, processorType, slaveCount } = hw;
  const mult = processorType === 'dual' ? 2 : 1;
  const storageBlock = splitStorage ? `SSD - 500GB (OS + Docker)\nHDD - ${ssd} (logs + image storage)` : `SSD - ${ssd}`;
  const cap = `(Required capacity (OS + Docker + app + alerts + 30% buffer): ~${storage.totalWithBufferGB.toFixed(0)} GB)`;
  let out = `Actual vCPU Required: ${requiredVCPU} vCPU\n\n`;

  if (setupType === 'single') {
    const isNUC = model.type === 'nuc';
    out += `${isNUC ? 'NUC BOX' : 'Dell Server (Single Processor)'}\n${model.name}\nvCPU - ${model.vcpu}\nRAM - ${isNUC ? model.ram : model.vcpu * 2}GB\n${storageBlock}\nIOPS - 5000+\n${cap}`;
  } else if (setupType === 'dual') {
    out += `Dell Server (Single Machine - Dual Processor)\nDual ${model.name}\n\nServer Specs:\nvCPU - ${model.vcpu * 2}\nRAM - ${model.vcpu * 4}GB\n${storageBlock}\nIOPS - 5000+\n${cap}`;
  } else {
    const vcpuPer = model.vcpu * mult;
    const ramPer = model.vcpu * 2 * mult;
    const storagePer = splitStorage ? `SSD - 500GB (OS + Docker)\nHDD - ${ssd}` : `SSD - ${ssd}`;
    out += `Multi-Server Setup\n\nMaster Node:\nvCPU - 8\nRAM - 16GB\nSSD - 500GB\nIOPS - 5000+\n\nSlave Nodes --- ${slaveCount} No.s\nDell Server (per slave - ${mult === 2 ? 'Dual' : 'Single'} Processor)\n${mult === 2 ? 'Dual ' : ''}${model.name}\n\nPer Slave Specs:\nvCPU - ${vcpuPer}\nRAM - ${ramPer}GB\n${storagePer}\nIOPS - 5000+\n\nTotal Infrastructure:\nTotal vCPU (Master + Slaves) - ${8 + slaveCount * vcpuPer}\nTotal RAM (Master + Slaves) - ${16 + slaveCount * ramPer}GB\nTotal K8s Overhead: ${slaveCount * 3} vCPU (3 per node × ${slaveCount} nodes)\n${cap}`;
  }
  if (!hw.compatible) {
    out += `\n\nWARNING: NOT COMPATIBLE!\nRequired vCPU: ${requiredVCPU}\nAvailable vCPU: ${hw.effectiveVCPU}\nDeficit: ${requiredVCPU - hw.effectiveVCPU} vCPU\n\nRecommendation: Select a higher spec server or switch to Dual/Multi-Server setup.`;
  }
  if (enableGPU && (cores.gpuL4 > 0 || cores.gpu4090 > 0)) {
    out += '\n\nGPU Configuration:';
    if (cores.gpuL4 > 0) out += `\nL4 GPU - ${cores.gpuL4} No.s (Object Detection)`;
    if (cores.gpu4090 > 0) out += `\nRTX 4090 GPU - ${cores.gpu4090} No.s (Face Recognition)`;
  }
  if (bufferPercentage > 0) {
    out += `\n\nBuffer Configuration:\nBase vCPU: ${baseVCPU}\nBuffer: ${bufferPercentage}% (+${requiredVCPU - baseVCPU} vCPU)\nTotal with Buffer: ${requiredVCPU} vCPU`;
  }
  return out;
}

export function buildShortSummary({ hw, requiredVCPU, storage, splitStorage }) {
  const ssd = getNearestSSDSize(storage.totalWithBufferGB);
  const { model, setupType, processorType, slaveCount } = hw;
  let ram;
  if (setupType === 'single') ram = model.type === 'nuc' ? model.ram : model.vcpu * 2;
  else if (setupType === 'dual') ram = model.vcpu * 4;
  else ram = 16 + slaveCount * model.vcpu * 2 * (processorType === 'dual' ? 2 : 1);
  const disk = splitStorage ? `SSD - 500GB\nHDD - ${ssd}` : `SSD - ${ssd}`;
  return `Actual vCPU Required: ${requiredVCPU} vCPU\n\nServer Specs:\nvCPU - ${hw.effectiveVCPU}\nRAM - ${ram}GB\n${disk}\nIOPS - 5000+`;
}
